import React, { useEffect, useState } from 'react';
import { ShieldCheck, ShieldAlert, ShieldX, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Compact pill + expandable tooltip that tells the user how reliable the
 * current station's analytics are. 4 tiers (A/B/C/D) — driven by the
 * `/api/broadcast/data-quality/{id}` endpoint.
 *
 * We show this instead of silently computing KPIs from insufficient data.
 */
const DataQualityBadge = ({ stationId, token, showDetails = false }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!stationId || !token) return;
    let cancelled = false;
    fetch(`${API}/api/broadcast/data-quality/${stationId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [stationId, token]);

  if (!data?.tier_meta) return null;

  const { tier, tier_meta, metrics } = data;
  const Icon =
    tier === 'A' ? ShieldCheck
    : tier === 'B' ? Shield
    : tier === 'C' ? ShieldAlert
    : ShieldX;

  const tone = {
    green:  'text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
    yellow: 'text-amber-300   bg-amber-500/10   border-amber-500/30',
    orange: 'text-orange-300  bg-orange-500/10  border-orange-500/30',
    red:    'text-red-300     bg-red-500/10     border-red-500/30',
  }[tier_meta.color] || 'text-white/70 bg-white/5 border-white/10';

  return (
    <div
      className={`inline-flex flex-col items-start gap-1 text-[11px] px-2.5 py-1.5 rounded-md border ${tone}`}
      data-testid={`data-quality-badge-${tier}`}
      title={tier_meta.description}
    >
      <div className="inline-flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" />
        <span className="font-medium">{tier_meta.label}</span>
        <span className="opacity-60">· Tier {tier}</span>
      </div>
      {showDetails && (
        <div className="text-[10px] opacity-75 leading-snug pl-5">
          {tier_meta.description}
          <div className="mt-1 opacity-60">
            {metrics.total_plays_7d} plays / 7d · {metrics.plays_per_hour_24h}/hr · duration {metrics.duration_coverage_pct}%
          </div>
        </div>
      )}
    </div>
  );
};

export default DataQualityBadge;
